import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApp } from '@/hooks/use-app-context';
import { Button } from '@/components/ui/button';
import { Icon } from '@/lib/icons';
import { isLanguageProfileIncomplete } from '@/lib/language-profile';

const DISMISS_KEY = 'yodi-phone-banner-dismissed';

export function ProfileCompletionBanner() {
  const { user, isAuthenticated, t } = useApp();
  const navigate = useNavigate();
  const { lang } = useParams<{ lang: string }>();
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try {
      setDismissed(sessionStorage.getItem(DISMISS_KEY) === 'true');
    } catch {}
  }, []);

  if (!isAuthenticated || !user) {
    return null;
  }

  const needsLanguageSetup =
    user.requiresLanguageSetup ?? isLanguageProfileIncomplete(user);

  if (needsLanguageSetup) {
    return (
      <div className="border-b border-brand bg-brand dark:bg-brand/30">
        <div className="mx-auto flex max-w-7xl flex-col gap-2 px-4 py-2.5 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
          <div className="flex items-center gap-2.5 text-sm text-brand">
            <Icon name="globe" className="h-4 w-4 flex-shrink-0" />
            <span>{t.languageProfileCompletionBanner}</span>
          </div>
          <Button
            size="sm"
            onClick={() => navigate(`/${lang || 'fr'}/settings?tab=profile`)}
            className="h-7 shrink-0 bg-primary text-xs text-white hover:bg-brand"
          >
            {t.languageProfileCompletionAction}
          </Button>
        </div>
      </div>
    );
  }

  if (user.phoneNumber || dismissed) {
    return null;
  }

  const rolesRequiringPhone = ['contributor', 'moderator', 'collecteur', 'COMPTABLE', 'admin', 'super_admin'];
  if (!rolesRequiringPhone.includes(user.role)) {
    return null;
  }

  const handleDismiss = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem(DISMISS_KEY, 'true');
    } catch {}
  };

  const handleAction = () => {
    handleDismiss();
    navigate(`/${lang || 'fr'}/settings`);
  };

  return (
    <div className="bg-brand dark:bg-brand/30 border-b border-brand">
      <div className="max-w-7xl mx-auto px-4 py-2.5 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
        <div className="flex items-center gap-2.5 text-sm text-brand">
          <Icon name="phone" className="h-4 w-4 flex-shrink-0" />
          <span className="line-clamp-2 sm:line-clamp-none">{t.profileCompletionBanner}</span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDismiss}
            className="h-7 text-xs text-brand hover:text-brand"
          >
            {t.profileCompletionDismiss}
          </Button>
          <Button
            size="sm"
            onClick={handleAction}
            className="h-7 text-xs bg-primary hover:bg-brand text-white"
          >
            {t.profileCompletionAction}
          </Button>
        </div>
      </div>
    </div>
  );
}
